import { env } from "@plura/shared/utils";
import slugify from "slugify";

export async function uploadToS3(
  file: File,
  filePath: string,
  onProgress: (progress: number) => void
): Promise<string> {
  const fileName = slugify(file.name);
  const partSize = 5 * 1024 * 1024; // 5 MB part size
  const totalParts = Math.ceil(file.size / partSize);

  // Step 1: Fetch pre-signed URLs and UploadId from the server
  const response = await fetch(
    `/api/presigned-multipart?fileName=${fileName}&filePath=${filePath}&partCount=${totalParts}&contentType=${file.type}`
  );
  const { uploadId, presignedUrls } = await response.json();

  if (!uploadId || !presignedUrls) {
    throw new Error('Failed to initialize multipart upload');
  }

  const parts = [];

  // Step 2: Upload each part using pre-signed URLs
  for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
    const start = (partNumber - 1) * partSize;
    const end = Math.min(partNumber * partSize, file.size);
    const part = file.slice(start, end);

    const presignedUrl = presignedUrls[partNumber - 1];

    let retries = 3;
    let uploadSuccessful = false;
    let eTag;

    while (retries > 0 && !uploadSuccessful) {
      try {
        const response = await fetch(presignedUrl, {
          method: 'PUT',
          body: part,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!response.ok) throw new Error('Upload failed');
        eTag = response.headers.get('ETag'); // S3 returns ETag in the response
        uploadSuccessful = true;
      } catch (error) {
        console.error(`Attempt failed for part ${partNumber}, retries left: ${retries - 1}`, error);
        retries--;
        if (retries === 0) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    if (!eTag) throw new Error(`Failed to upload part ${partNumber}`);

    parts.push({
      PartNumber: partNumber,
      ETag: eTag,
    });

    const percentage = Math.round((partNumber / totalParts) * 100);
    onProgress(percentage);
  }

  // Step 3: Complete the upload (call another API route)
  const completeResponse = await fetch('/api/complete-multipart', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fileName,
      filePath,
      uploadId,
      parts,
    }),
  });

  if (!completeResponse.ok) {
    throw new Error('Failed to complete multipart upload');
  }

  const fileUrl = `https://s3.amazonaws.com/${env.NEXT_PUBLIC_AMPLIFY_BUCKET}/${filePath}/${fileName}`.trim();
  return fileUrl;
}